import { FC, useEffect, useState } from 'react';
import { Loader } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';

import { useIsMobile } from '@/hooks/use-mobile';

import { useAuthStore } from '@/stores/authStore';
import { useGlobalStore } from '@/stores/globalStore';

import { useWsClientStore } from '@/services/ws-client';

import { USER_ROLES } from '@/constants/global-constants';
import {
  adminRoutes,
  controllerRoutes,
  executorRoutes,
  publicRoutes,
} from '@/constants/route-settings';
import { ExplorerItemType } from '@/constants/table-columns-config';

import { LayoutDesktopWidget } from './LayoutDesktopWidget';
import { LayoutMobileWidget } from './LayoutMobileWidget';

export const LayoutWidget: FC = () => {
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const { webSocketReady } = useWsClientStore();

  const { getGlobalSettings } = useGlobalStore();

  const navigateBasedOnRole = (userType: string) => {
    switch (userType) {
      case USER_ROLES.ADMIN.value:
        navigate(adminRoutes.users);
        break;
      case USER_ROLES.CONTROLLER.value:
        navigate(`${controllerRoutes.myDocuments}`, {
          state: {
            uuid: '',
            entityType: ExplorerItemType.AGREEMENT,
          },
        });
        break;
      case USER_ROLES.EXECUTOR.value:
        navigate(`${executorRoutes.myDocuments}`, {
          state: {
            uuid: '',
            entityType: ExplorerItemType.AGREEMENT,
          },
        });
        break;
    }
  };

  useEffect(() => {
    async function loadData() {
      try {
        await getGlobalSettings();
      } catch (error) {
        navigate(publicRoutes.login);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (location.pathname === publicRoutes.root) {
      navigateBasedOnRole(user?.role || '');
    }
  }, [location, user]);

  if (isLoading || !webSocketReady) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader className="animate-spin-slow" />
      </div>
    );
  }

  // Return the main layout component with proper structure for scroll management
  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden">
      {isMobile ? <LayoutMobileWidget /> : <LayoutDesktopWidget />}
    </div>
  );
};
